import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Text,
    HStack,
    Button,
    Box,
    Badge,
    useToast,
    Textarea,
    Divider,
} from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import {
    ICellEditorProps,
    ICellTextProps,
    IGroupRowProps,
} from "ka-table/props";
import { EditingMode } from "ka-table/enums";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import {
    AppAlertMessage,
    AppFormControl,
    AppIcon,
    AppSelect,
    AppSelectOption,
    KaPageSizeSelector,
} from "../../../components";
import {
    InternshipSchedule,
    InternshipScheduleApi,
    InternshipScheduleSeat,
    StudentSkill,
    StudentSkillApi,
    StudentSkillComment,
    StudentSkillCommentApi,
    UserApi,
} from "../../../apis";
import { APPC, cPath } from "../../../config";

export const InternshipScheduleStudentSkillList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    setList: (newList: any[]) => void;
    internshipScheduleSeat: InternshipScheduleSeat;
    loadingList?: boolean;
}> = ({
    table,
    paging,
    list,
    setList,
    internshipScheduleSeat,
    loadingList = false,
}) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { grant, user } = useAuthData();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const {
        id: internshipId,
        internshipScheduleId,
        studentId,
        internshipScheduleSeatId,
    } = useParams<{
        id: string;
        internshipScheduleId: string;
        studentId: string;
        internshipScheduleSeatId: string;
    }>();

    // state & const
    const [loading, setLoading] = useState(false);
    const [comment, setComment] = useState<StudentSkillComment>(
        new StudentSkillComment()
    );
    const [isEditable, setIsEditable] = useState(false);
    const [isGestureAlertOpen, setIsGestureAlertOpen] = useState(false);
    const [assignListGestures, setAssignListGestures] = useState<any[]>([]);
    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const isCompany =
        window.location.pathname ===
        cPath("ADMIN.INTERNSHIP_SCHEDULE_STUDENT_SKILL_PAGE", {
            id: internshipId,
            internshipScheduleId,
            studentId,
        });

    const columns: Column[] = [
        {
            key: "name",
            title: t("ent.InternshipScheduleSkill:name.label"),
            dataType: DataType.String,
            // width: "66%",
            isEditable: false,
            isSortable: false,
        },
        {
            key: "skill.name",
            title: t("padm.SkillPage:text.pageTitle"),
            dataType: DataType.String,
            isEditable: false,
            isSortable: false,
        },
        {
            key: "gestures",
            title: t("ent.InternshipScheduleSkill:gestures.label"),
            dataType: DataType.Object,
            width: 50,
            isEditable: false,
            isSortable: false,
        },
        {
            key: "rate",
            title: t("padm.InternshipScheduleStudentSkillPage:text.rating"),
            dataType: DataType.Object,
            width: 250,
            isEditable: true,
            isSortable: false,
        },
        {
            key: "finalRate",
            title: t(
                "padm.InternshipScheduleStudentSkillPage:text.finalRating"
            ),
            dataType: DataType.Object,
            width: 250,
            isEditable: true,
            isSortable: false,
        },
    ];

    // handler
    const fetchCommentData = () => {
        if (
            !isCompany &&
            !(internshipScheduleSeat?.internshipSchedule as InternshipSchedule)
                ?.id
        ) {
            return;
        }
        setLoading(true);
        const params = {
            "internshipSchedule.id": 0,
            "student.id": 0,
        };
        if (internshipScheduleId && studentId) {
            params["internshipSchedule.id"] = Number(internshipScheduleId);
            params["student.id"] = Number(studentId);
        } else if (internshipScheduleSeatId && studentId) {
            params["internshipSchedule.id"] = (
                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
            )?.id;
            params["student.id"] = Number(studentId);
        } else if (internshipScheduleSeatId && user?.id) {
            params["internshipSchedule.id"] = (
                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
            )?.id;
            params["student.id"] = user.id;
        }

        StudentSkillCommentApi.getCollection<StudentSkillComment>(
            1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null && response.totalItems > 0) {
                    setComment(response.items[0]);
                }
            })
            .finally(() => setLoading(false));
    };

    const onCommentSubmitHandler = () => {
        if (!comment) return;

        setLoading(true);

        const payload = {
            internshipSchedule:
                InternshipScheduleApi.toResourceIRI(internshipScheduleId),
            student: UserApi.toResourceIRI(studentId),
            commentFinalReport: comment.commentFinalReport,
            commentPositivePoint: comment.commentPositivePoint,
            commentAreasOfImprovement: comment.commentAreasOfImprovement,
            finalCommentFinalReport: comment.finalCommentFinalReport,
            finalCommentPositivePoint: comment.finalCommentPositivePoint,
            finalCommentAreasOfImprovement:
                comment.finalCommentAreasOfImprovement,
        } as StudentSkillComment;

        StudentSkillCommentApi.createOrUpdate<
            StudentSkillComment,
            StudentSkillComment
        >(comment.id ?? 0, payload)
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    setComment(response);
                }
            })
            .finally(() => setLoading(false));
    };

    const onRateSubmitHandler = (
        studentSkillId: string,
        payload: StudentSkill
    ) => {
        StudentSkillApi.createOrUpdate<StudentSkill, StudentSkill>(
            studentSkillId,
            payload
        ).then(({ response, errorMessage }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                toast({
                    title: t("cmn:message.save.success"),
                    status: "success",
                });
            }
        });
    };

    // useEffect
    useEffect(() => {
        fetchCommentData();
    }, [internshipScheduleSeat]);

    useEffect(() => {
        const editable = !!(
            grant.isInternshipCoordinator ||
            grant.isInternshipSupervisor ||
            grant.isInternshipTutor ||
            (grant.isSchoolAndCompany &&
                activeSchoolOrCompany &&
                activeSchoolOrCompany?.["@type"] ===
                    APPC.BE.Category.TYPE.TYPE_COMPANY)
        );
        setIsEditable(editable);
    }, [grant, activeSchoolOrCompany]);

    // render
    const CustomRateEditor = ({
        column,
        rowKeyValue,
        rowData,
        setList,
        isFinalRate = false,
    }: ICellEditorProps & {
        setList: (newList: any[]) => void;
        isFinalRate?: boolean;
    }) => {
        const fieldKey = isFinalRate ? "finalRate" : "rate";

        const { t } = useTrans();
        const [selectedRate, setSelectedRate] =
            useState<AppSelectOption | null>(
                rowData[fieldKey]?.value ? rowData[fieldKey] : null
            );

        const payloadData = {
            internshipSchedule:
                InternshipScheduleApi.toResourceIRI(internshipScheduleId),
            skillCriteria: rowData["@id"],
            student: UserApi.toResourceIRI(studentId),
        };
        payloadData[fieldKey] =
            selectedRate?.value !== undefined && selectedRate?.value !== null
                ? String(selectedRate.value)
                : null;

        const payload = payloadData as StudentSkill;

        const close = () => {
            table.closeEditor(rowKeyValue, column.key);
        };

        return (
            <>
                <AppSelect<AppSelectOption>
                    isClearable
                    options={Object.entries(APPC.BE.StudentSkill.RATE).map(
                        ([key, value]) => ({
                            value,
                            label: t(`con.StudentSkill:${key}`),
                        })
                    )}
                    value={selectedRate || null}
                    placeholder={t("ent.StudentSkill:rate.placeholder")}
                    onChange={(e) => {
                        setSelectedRate(e as AppSelectOption | null);
                    }}
                    styles={{
                        container: (base) => ({
                            ...base,
                            maxWidth: "550px",
                        }),
                    }}
                />
                <HStack mt={3} justifyContent={"flex-end"}>
                    <Button size={"xs"} variant={"outline"} onClick={close}>
                        {t("cmn:button.cancel")}
                    </Button>
                    <Button
                        size={"xs"}
                        onClick={() => {
                            const updatedRow = {
                                ...rowData,
                                [fieldKey]: selectedRate,
                            };

                            const newList = list.map((item) =>
                                item.id === rowKeyValue ? updatedRow : item
                            );

                            setList(newList);
                            onRateSubmitHandler(
                                rowData.studentSkillId,
                                payload
                            );
                            close();
                        }}
                    >
                        {t("cmn:button.save")}
                    </Button>
                </HStack>
            </>
        );
    };

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        groups: [{ columnKey: "skill.name" }],
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellEditor: {
                content: (props: ICellEditorProps) => {
                    switch (props.column.key) {
                        case "rate":
                        case "finalRate":
                            return (
                                <CustomRateEditor
                                    {...props}
                                    setList={setList}
                                    isFinalRate={
                                        props.column.key === "finalRate"
                                    }
                                />
                            );
                    }
                },
            },
            groupCell: {
                content: (props: IGroupRowProps) => {
                    switch (props.column.key) {
                        case "skill.name":
                            return (
                                <Text
                                    textStyle={"mediumSm"}
                                    color={"var(--chakra-colors-black)"}
                                    whiteSpace={"collapse"}
                                >
                                    {props?.groupKey[0]}
                                </Text>
                            );
                    }
                },
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "name":
                            return (
                                <>
                                    <Text
                                        textStyle={"regularSm"}
                                        color={"var(--chakra-colors-black)"}
                                        whiteSpace={"collapse"}
                                    >
                                        {props?.rowData?.name}
                                    </Text>
                                </>
                            );
                        case "gestures":
                            return (
                                <HStack
                                    cursor="pointer"
                                    justifyContent={"center"}
                                    onClick={() => {
                                        setAssignListGestures(
                                            props.rowData.gestures || []
                                        );
                                        setIsGestureAlertOpen(true);
                                    }}
                                >
                                    <AppIcon
                                        name={"icl-eye"}
                                        color={"var(--chakra-colors-primary)"}
                                    />
                                </HStack>
                            );
                        case "rate":
                        case "finalRate":
                            return (
                                <HStack
                                    cursor={"pointer"}
                                    onClick={() => {
                                        if (isEditable) {
                                            table.openEditor(
                                                props.rowKeyValue,
                                                props.column.key
                                            );
                                        }
                                    }}
                                >
                                    {isEditable && (
                                        <Box>
                                            <AppIcon
                                                name={"icl-pencil"}
                                                color={
                                                    "var(--chakra-colors-primary)"
                                                }
                                            />
                                        </Box>
                                    )}
                                    {props?.rowData?.[props?.column?.key]
                                        ?.value && (
                                        <HStack flexWrap="wrap">
                                            <Badge
                                                variant="secondary"
                                                maxW="360px"
                                                isTruncated
                                            >
                                                {
                                                    props?.rowData?.[
                                                        props?.column?.key
                                                    ]?.label
                                                }
                                            </Badge>
                                        </HStack>
                                    )}
                                </HStack>
                            );
                    }
                },
            },
        },
    };

    if (isEditable && list?.length) {
        kaTableProps.editableCells = list.map((item) => ({
            columnKey: "rate",
            rowKeyValue: item.id,
        }));
        kaTableProps.editingMode = EditingMode.Cell;
    }

    return (
        <>
            <KaTable {...kaTableProps} />
            <Box mt={3} w="full">
                <Text textStyle="heading5" mb={3}>
                    {t(
                        "padm.InternshipScheduleStudentSkillPage:text.intermediateComments"
                    )}
                </Text>
                <HStack w="full" align="stretch" spacing={4}>
                    <AppFormControl
                        label={t(
                            "ent.StudentSkillComment:commentAreasOfImprovement.label"
                        )}
                        isLoading={loading}
                    >
                        <Textarea
                            readOnly={!isEditable}
                            flex={1}
                            minH="172px"
                            resize="none"
                            placeholder={t(
                                "ent.StudentSkillComment:commentAreasOfImprovement.placeholder"
                            )}
                            value={comment.commentAreasOfImprovement ?? ""}
                            onChange={(e) =>
                                setComment(
                                    new StudentSkillComment({
                                        ...comment,
                                        commentAreasOfImprovement:
                                            e.target.value,
                                    })
                                )
                            }
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t(
                            "ent.StudentSkillComment:commentPositivePoint.label"
                        )}
                        isLoading={loading}
                    >
                        <Textarea
                            readOnly={!isEditable}
                            flex={1}
                            minH="172px"
                            resize="none"
                            placeholder={t(
                                "ent.StudentSkillComment:commentPositivePoint.placeholder"
                            )}
                            value={comment.commentPositivePoint ?? ""}
                            onChange={(e) =>
                                setComment(
                                    new StudentSkillComment({
                                        ...comment,
                                        commentPositivePoint: e.target.value,
                                    })
                                )
                            }
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t(
                            "ent.StudentSkillComment:commentFinalReport.label"
                        )}
                        isLoading={loading}
                    >
                        <Textarea
                            readOnly={!isEditable}
                            flex={1}
                            minH="172px"
                            resize="none"
                            placeholder={t(
                                "ent.StudentSkillComment:commentFinalReport.placeholder"
                            )}
                            value={comment.commentFinalReport ?? ""}
                            onChange={(e) =>
                                setComment(
                                    new StudentSkillComment({
                                        ...comment,
                                        commentFinalReport: e.target.value,
                                    })
                                )
                            }
                        />
                    </AppFormControl>
                </HStack>
                <Divider mt={3} />
                <Text textStyle="heading5" my={3}>
                    {t(
                        "padm.InternshipScheduleStudentSkillPage:text.finalComments"
                    )}
                </Text>
                <HStack w="full" align="stretch" spacing={4}>
                    <AppFormControl
                        label={t(
                            "ent.StudentSkillComment:commentAreasOfImprovement.label"
                        )}
                        isLoading={loading}
                    >
                        <Textarea
                            readOnly={!isEditable}
                            flex={1}
                            minH="172px"
                            resize="none"
                            placeholder={t(
                                "ent.StudentSkillComment:commentAreasOfImprovement.placeholder"
                            )}
                            value={comment.finalCommentAreasOfImprovement ?? ""}
                            onChange={(e) =>
                                setComment(
                                    new StudentSkillComment({
                                        ...comment,
                                        finalCommentAreasOfImprovement:
                                            e.target.value,
                                    })
                                )
                            }
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t(
                            "ent.StudentSkillComment:commentPositivePoint.label"
                        )}
                        isLoading={loading}
                    >
                        <Textarea
                            readOnly={!isEditable}
                            flex={1}
                            minH="172px"
                            resize="none"
                            placeholder={t(
                                "ent.StudentSkillComment:commentPositivePoint.placeholder"
                            )}
                            value={comment.finalCommentPositivePoint ?? ""}
                            onChange={(e) =>
                                setComment(
                                    new StudentSkillComment({
                                        ...comment,
                                        finalCommentPositivePoint:
                                            e.target.value,
                                    })
                                )
                            }
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t(
                            "ent.StudentSkillComment:commentFinalReport.label"
                        )}
                        isLoading={loading}
                    >
                        <Textarea
                            readOnly={!isEditable}
                            flex={1}
                            minH="172px"
                            resize="none"
                            placeholder={t(
                                "ent.StudentSkillComment:commentFinalReport.placeholder"
                            )}
                            value={comment.finalCommentFinalReport ?? ""}
                            onChange={(e) =>
                                setComment(
                                    new StudentSkillComment({
                                        ...comment,
                                        finalCommentFinalReport: e.target.value,
                                    })
                                )
                            }
                        />
                    </AppFormControl>
                </HStack>
                <AppAlertMessage
                    isOpen={isGestureAlertOpen}
                    onCancel={() => setIsGestureAlertOpen(false)}
                    size="lg"
                >
                    <Box p={4}>
                        {assignListGestures.length === 0 && (
                            <Text>
                                {t(
                                    "padm.InternshipScheduleStudentSkillPage:text.noGesturesAvailable"
                                )}
                            </Text>
                        )}
                        <HStack flexWrap="wrap" spacing={2}>
                            {assignListGestures.map((gesture) => (
                                <Badge key={gesture.id} variant="secondary">
                                    {gesture.name}
                                </Badge>
                            ))}
                        </HStack>
                    </Box>
                </AppAlertMessage>
                {isEditable && (
                    <HStack w="full" justify="flex-end" mt={3}>
                        <Button size="sm" onClick={onCommentSubmitHandler}>
                            {t("cmn:button.save")}
                        </Button>
                    </HStack>
                )}
            </Box>
        </>
    );
};
